import { useEffect, useMemo } from 'react'
import { P, match } from 'ts-pattern'

import { useQuery } from '@sequencehq/api'
import {
  type Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'

import { analyseAutomation } from './utils/analyseAutomation'
import { logSimulationRun } from './utils/simulationDeveloperLog'
import { mergeSimulationInputDefaults } from './utils/mergeSimulationInputDefaults'
import { parseConductorWorkflow } from './utils/parseConductorWorkflow'
import { useDependencyResolution } from './useDependencyResolution'
import { useEvaluation } from './useEvaluation'
import { useSimulationTelemetry } from './useSimulationTelemetry'
import type {
  AutomationAnalysis,
  SimulationResult,
  TerminalTaskMatcher,
  TerminalType
} from './simulation.types'

/**
 * The values a workflow is started with. They come from outside the workflow,
 * which is why a `${workflow.input.*}` reference is never something we go and
 * fetch - whoever is simulating has to pass it in.
 *
 *   QUOTE_REQUIRES_APPROVAL declares four start inputs:
 *     quoteId, requestorId    -> the caller gives us these, below
 *     comment                 -> we default this ourselves, in the hook
 *     signatureConfiguration  -> deliberately left alone
 */
export type WorkflowInputByTriggerType = {
  QUOTE_REQUIRES_APPROVAL: { quoteId: string; requestorId: string }
}

export type WorkflowInput =
  WorkflowInputByTriggerType[keyof WorkflowInputByTriggerType]

export type UseAutomationSimulationProps = {
  /** Simulate whichever version of this trigger's automation is live. */
  triggerType?: Dashboardv99990101Api.Automations.TriggerType
  /** Or simulate one specific version. You need both of these together. */
  automationId?: string
  versionId?: string
  workflowInput: WorkflowInput
  terminalMatchers?: TerminalTaskMatcher[]
  terminalPrecedence?: TerminalType[]
  /** Values we're happy to take from the cache instead of fetching again. */
  useCacheValueFor?: string[]
  enabled?: boolean
}

export type AutomationSimulation = {
  analysis: AutomationAnalysis | undefined
  /**
   * Undefined for the duration of a run, so anything showing it directly blinks
   * every time it re-runs. Callers that don't want that hold the last answer
   * themselves and watch `isPending` - see `useQuoteApproval`.
   */
  result: SimulationResult | undefined
  isPending: boolean
  isLoadingAutomation: boolean
  isResolvingDependencies: boolean
  isEvaluating: boolean
  refetchDependencies: (semanticTypes: string[]) => Promise<void>
}

/**
 * Works out where an automation will end up for the thing being edited, so we
 * can tell someone what will happen before they commit to it.
 *
 * There are three steps, and `result` stays undefined until the last one the
 * run needs has finished. We read the workflow and work out every route through
 * it, go and fetch the data those routes read, then run their conditions.
 *
 *   read the workflow -> fetch what it reads -> run the conditions
 */
export const useAutomationSimulation = (
  props: UseAutomationSimulationProps
): AutomationSimulation => {
  const isEnabled = props.enabled ?? true
  const hasExplicitVersion = Boolean(props.automationId && props.versionId)

  const activeVersionQuery = useQuery(
    dashboardv99990101Client.getAllAutomationVersions,
    { triggerType: props.triggerType, active: true },
    {
      enabled: isEnabled && !hasExplicitVersion && Boolean(props.triggerType),
      staleTime: 5 * MINUTES
    }
  )

  const explicitVersionQuery = useQuery(
    dashboardv99990101Client.getAutomationVersion,
    {
      automationId: props.automationId ?? '',
      versionId: props.versionId ?? ''
    },
    { enabled: isEnabled && hasExplicitVersion, staleTime: 5 * MINUTES }
  )

  const automationVersion = hasExplicitVersion
    ? explicitVersionQuery.data
    : activeVersionQuery.data?.items.at(0)

  // `isFetching` rather than `isPending`, because a disabled query counts as
  // pending forever. We'd be telling the caller to wait for a simulation that
  // is never going to start.
  const isLoadingAutomation = hasExplicitVersion
    ? explicitVersionQuery.isFetching
    : activeVersionQuery.isFetching

  // Compared as text rather than by identity. A caller writing the matchers
  // inline gives us a brand new array every render, and if we keyed the
  // analysis on that we'd throw away the analysis - and restart the fetching
  // that hangs off it - on every single render.
  const terminalsKey = JSON.stringify([
    props.terminalMatchers ?? null,
    props.terminalPrecedence ?? null
  ])

  // Timed as it's worked out rather than watched from outside, because this
  // happens while we render and is over before anything else can look.
  const { analysis, analysisDurationMs } = useMemo(() => {
    const startedAt = performance.now()

    const workflow = parseConductorWorkflow(
      automationVersion?.workflowDefinition
    )
    if (!workflow) return { analysis: undefined, analysisDurationMs: undefined }

    const [terminalMatchers, terminalPrecedence] = JSON.parse(terminalsKey)

    const analysed = analyseAutomation({
      workflow,
      terminalMatchers: terminalMatchers ?? undefined,
      terminalPrecedence: terminalPrecedence ?? undefined
    })

    return {
      analysis: analysed,
      analysisDurationMs: Math.round(performance.now() - startedAt)
    }
  }, [automationVersion, terminalsKey])

  // Serialised for the same reason as the matchers above.
  const workflowInputKey = JSON.stringify(props.workflowInput ?? {})

  /**
   * What a run is simulated with: the caller's inputs over the defaults we fill
   * in for them. Worked out here rather than inside the fetching, because
   * evaluating the conditions needs the same values.
   */
  const workflowInput = useMemo(
    () =>
      mergeSimulationInputDefaults({
        triggerType: automationVersion?.triggerType ?? props.triggerType,
        workflowInput: JSON.parse(workflowInputKey)
      }),
    [automationVersion, props.triggerType, workflowInputKey]
  )

  const { isResolvingDependencies, refetchDependencies, resolution } =
    useDependencyResolution({
      analysis,
      isEnabled,
      useCacheValueFor: props.useCacheValueFor,
      workflowInput,
      workflowInputKey
    })

  const { evaluation, evaluationDurationMs, expressionResults, isEvaluating } =
    useEvaluation({
      analysis,
      isEnabled,
      resolution,
      workflowInput
    })

  const result = useMemo<SimulationResult | undefined>(() => {
    if (!isEnabled || isLoadingAutomation) return undefined

    if (!automationVersion) {
      return { outcome: 'INDETERMINATE', reason: 'NO_ACTIVE_AUTOMATION' }
    }

    if (!analysis) {
      return {
        outcome: 'INDETERMINATE',
        reason: 'MALFORMED_WORKFLOW_DEFINITION'
      }
    }

    if (isResolvingDependencies || !resolution) return undefined

    // A fetch that failed is the end of it, and its reason is the answer. Having
    // the data instead means the conditions still have to be run, and
    // `evaluation` is undefined until they have been.
    return match(resolution)
      .with({ reason: P.select() }, reason => ({
        outcome: 'INDETERMINATE' as const,
        reason
      }))
      .with({ taskOutputs: P.any }, () => evaluation)
      .exhaustive()
  }, [
    analysis,
    automationVersion,
    evaluation,
    isEnabled,
    isLoadingAutomation,
    isResolvingDependencies,
    resolution
  ])

  const isPending =
    isLoadingAutomation || isResolvingDependencies || isEvaluating

  useSimulationTelemetry({
    analysis,
    analysisDurationMs,
    evaluationDurationMs,
    isEnabled,
    isPending,
    isResolvingDependencies,
    result
  })

  // Yay, logs! Click on the lil' bug icon in the devtools to start logging them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the other three are what this answer was worked out from, so they can't have moved on without a new answer following
  useEffect(() => {
    if (!result) return

    logSimulationRun({ analysis, resolution, result, expressionResults })
  }, [result])

  return useMemo(
    () => ({
      analysis,
      result,
      isPending,
      isLoadingAutomation,
      isResolvingDependencies,
      isEvaluating,
      refetchDependencies
    }),
    [
      analysis,
      isEvaluating,
      isLoadingAutomation,
      isPending,
      isResolvingDependencies,
      refetchDependencies,
      result
    ]
  )
}
